// Canvelle customization (HORIZON-THEME.md §1.32) — custom file, survives Horizon upgrades.
// Loaded on every page by snippets/canvelle-a11y.liquid. Two accessibility repairs that
// Horizon's carousels need at runtime, because the markup is (re)built by its scripts:
//
// 1. Hidden carousel slides keep their links and buttons out of the Tab order.
//    Horizon marks off-screen slides aria-hidden="true" (and its CSS styles slides by
//    that attribute), but their links stay focusable, so keyboard users can tab to things
//    screen readers are told don't exist (axe aria-hidden-focus). `inert` is NOT used: it
//    would also block mouse clicks on half-visible "peeking" cards. Instead each focusable
//    element inside a hidden slide gets tabindex="-1" (its own tabindex is remembered in
//    data-cv-tabindex) and gets it back when the slide shows again.
//
// 2. Photo strips are keyboard-scrollable: product-card strips and the product page's
//    own gallery. They scroll (swipe, trackpad, arrows, thumbnails) but neither the strip
//    nor anything in its visible slide can take focus (axe scrollable-region-focusable).
//    Strips with more than one photo get tabindex="0", role="group" and a label
//    ("<product> photos"); the focus ring shows only for keyboard focus (:focus-visible —
//    styles in snippets/canvelle-a11y.liquid).

const FOCUSABLE =
  'a[href], area[href], button, input, select, textarea, iframe, audio[controls], video[controls], summary, [contenteditable="true"], [tabindex]';
const HIDDEN_SLIDE = 'slideshow-slide[aria-hidden="true"]';

/** @param {Element} el */
function park(el) {
  if (el.hasAttribute('data-cv-tabindex')) return;
  const tabindex = el.getAttribute('tabindex');
  if (tabindex === '-1') return; // already out of the Tab order
  el.setAttribute('data-cv-tabindex', tabindex ?? '');
  el.setAttribute('tabindex', '-1');
}

/** @param {Element} el */
function unpark(el) {
  const tabindex = el.getAttribute('data-cv-tabindex');
  el.removeAttribute('data-cv-tabindex');
  if (tabindex === '' || tabindex === null) el.removeAttribute('tabindex');
  else el.setAttribute('tabindex', tabindex);
}

/** Bring a slide's focusable content in line with its aria-hidden state. @param {Element} slide */
function syncSlide(slide) {
  if (slide.closest(HIDDEN_SLIDE)) {
    for (const el of slide.querySelectorAll(FOCUSABLE)) park(el);
  } else {
    // Unpark only what no longer sits inside any hidden slide (a nested slide may still be hidden).
    for (const el of slide.querySelectorAll('[data-cv-tabindex]')) {
      if (!el.closest(HIDDEN_SLIDE)) unpark(el);
    }
  }
}

const STRIPS = 'product-card slideshow-slides, media-gallery slideshow-slides';

/** @param {Element} strip - a product card's or the product gallery's slideshow-slides */
function labelStrip(strip) {
  if (strip.hasAttribute('data-cv-strip')) return;
  if (strip.closest('slideshow-component[disabled="true"]')) return;
  if (strip.querySelectorAll(':scope > slideshow-slide').length < 2) return;
  const card = strip.closest('product-card');
  const product = strip.closest('product-information, .product-information, dialog');
  const title = card
    ? card.querySelector('.product-card__link')?.getAttribute('aria-label') ||
      card.querySelector('[ref="cardGalleryLink"]')?.getAttribute('aria-label') ||
      ''
    : (product?.querySelector('h1, h2, .product-title, [class*="product__title"]') || document.querySelector('h1'))?.textContent?.trim() ||
      '';
  strip.setAttribute('data-cv-strip', '');
  strip.setAttribute('role', 'group');
  strip.setAttribute('aria-label', title ? `${title} photos` : 'Product photos');
  strip.setAttribute('tabindex', '0');
  // A strip inside a hidden slide (e.g. a card in a carousel) starts parked.
  if (strip.closest(HIDDEN_SLIDE)) park(strip);
}

/** @param {Element} root */
function scan(root) {
  for (const strip of root.matches?.(STRIPS) ? [root] : root.querySelectorAll?.(STRIPS) ?? []) {
    labelStrip(strip);
  }
  if (root.matches?.('slideshow-slide')) syncSlide(root);
  for (const slide of root.querySelectorAll?.('slideshow-slide') ?? []) syncSlide(slide);
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      if (mutation.target instanceof Element && mutation.target.matches('slideshow-slide')) syncSlide(mutation.target);
    } else {
      for (const node of mutation.addedNodes) if (node instanceof Element) scan(node);
    }
  }
}).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden'] });

scan(document.documentElement);
